import React from 'react'

import {
  Button,
  ModalBox,
  PriceString,
  Skeleton,
  Table,
  useModalContext,
} from '@fourthwall/components'
import { fixRounding } from '@fourthwall/utils/dist/number'

import type { EnabledCurrencies, PriceRates, Tier } from '../../../../types'
import { usePriceRatesQuery } from '../../hooks/usePriceRatesQuery'
import { currencyDecimalPlaces } from '../../providers/TierProvider/consts'
import type { FormValues } from '../../providers/TierProvider/types'
import { getExchangeRate } from '../../utils'
import * as S from './styled'

type Props = {
  tier: Tier
  price: FormValues['price']
  enabledCurrencies: EnabledCurrencies
  onSave: (priceRates: PriceRates) => void
}

export const CurrenciesUpdateModal = ({ tier, price, enabledCurrencies, onSave }: Props) => {
  const modal = useModalContext()

  const { priceRatesQuery } = usePriceRatesQuery(
    [{ usd_amount: fixRounding((parseFloat(price) || 1) * 100, 2) }],
    {
      cacheTime: 0,
      staleTime: Infinity,
    },
  )

  const getTableData = () => {
    if (!enabledCurrencies) return []
    if (tier && !('pricesAutomaticallyConverted' in tier)) return []

    return enabledCurrencies.map((currency) => {
      const currencyLowercase = currency.toLowerCase() as keyof typeof tier.variants.monthly.amounts

      const amount = tier.variants.monthly.amounts[currencyLowercase].amount

      const currentRate = amount / 10 ** currencyDecimalPlaces[currencyLowercase]

      const newRate = priceRatesQuery.data ? (
        getExchangeRate(currency, priceRatesQuery.data)
      ) : (
        <Skeleton.Shape height={21} width={140} />
      )

      return {
        currency,
        currentRate,
        newRate,
      }
    })
  }

  return (
    <ModalBox
      size="medium"
      title={
        <>
          Update currency conversion for <PriceString value={price} /> per month
        </>
      }
      buttons={[
        <Button label="Cancel" appearance="secondary" fullWidth onClick={modal.close} />,
        <Button
          label="Update"
          appearance="primary"
          fullWidth
          loading={!priceRatesQuery.data}
          onClick={() => {
            if (!priceRatesQuery.data) return

            modal.close()
            onSave(priceRatesQuery.data)
          }}
        />,
      ]}
      onClose={modal.close}
    >
      <S.TableContainer>
        <Table
          data={getTableData()}
          headers={[
            {
              id: 'currency',
              label: 'Currecy',
              render: ({ currency }) => <S.CurrencyContainer>{currency}</S.CurrencyContainer>,
            },
            {
              id: 'currentRate',
              label: 'Current value',
              render: ({ currentRate }) => currentRate,
            },
            {
              id: 'newRate',
              label: 'New value',
              render: ({ newRate }) => <S.Bold>{newRate}</S.Bold>,
            },
          ]}
          minWidth={0}
          compact
        />
      </S.TableContainer>
    </ModalBox>
  )
}
